import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Plus, CalendarIcon, RefreshCw, Filter, BookOpen, ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JournalEntriesPage() {
  const { getAuthHeader } = useAuth();
  
  // Data states
  const [entries, setEntries] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  
  // Form states
  const [entryForm, setEntryForm] = useState({
    entry_date: new Date(),
    debit_classification_id: '',
    debit_classification_name: '',
    debit_linked_entity_type: null,
    debit_linked_entity_id: '',
    debit_linked_entity_name: '',
    debit_account_type: '',
    credit_classification_id: '',
    credit_classification_name: '',
    credit_linked_entity_type: null,
    credit_linked_entity_id: '',
    credit_linked_entity_name: '',
    credit_account_type: '',
    amount: '',
    remarks: ''
  });
  
  // Filter states
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });

  // Memoized sorted options
  const classificationOptions = useMemo(() => {
    return [...classifications]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ value: c.id, label: c.name, data: c }));
  }, [classifications]);

  const clientOptions = useMemo(() => {
    return [...clients]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(c => ({ value: c.id, label: c.name }));
  }, [clients]);

  const supplierOptions = useMemo(() => {
    return [...suppliers]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(s => ({ value: s.id, label: s.name }));
  }, [suppliers]);

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter.start) params.append('start_date', format(dateFilter.start, 'yyyy-MM-dd'));
      if (dateFilter.end) params.append('end_date', format(dateFilter.end, 'yyyy-MM-dd'));
      
      const response = await axios.get(`${API_URL}/api/journal-entries?${params}`, {
        headers: getAuthHeader()
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    }
  }, [getAuthHeader, dateFilter]);

  const fetchClassifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-transactions/classifications`, {
        headers: getAuthHeader()
      });
      setClassifications(response.data.classifications || []);
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
    }
  }, [getAuthHeader]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, {
        headers: getAuthHeader()
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [getAuthHeader]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers`, {
        headers: getAuthHeader()
      });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEntries(),
        fetchClassifications(),
        fetchClients(),
        fetchSuppliers()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchEntries, fetchClassifications, fetchClients, fetchSuppliers]);

  useEffect(() => {
    fetchEntries();
  }, [dateFilter, fetchEntries]);

  const handleDebitClassificationChange = (classificationId) => {
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) return;

    setEntryForm(prev => ({
      ...prev,
      debit_classification_id: classificationId,
      debit_classification_name: classification.name,
      debit_account_type: classification.account_type,
      debit_linked_entity_type: classification.linked_entity_type || null,
      debit_linked_entity_id: '',
      debit_linked_entity_name: ''
    }));
  };

  const handleCreditClassificationChange = (classificationId) => {
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) return;

    setEntryForm(prev => ({
      ...prev,
      credit_classification_id: classificationId,
      credit_classification_name: classification.name,
      credit_account_type: classification.account_type,
      credit_linked_entity_type: classification.linked_entity_type || null,
      credit_linked_entity_id: '',
      credit_linked_entity_name: ''
    }));
  };

  const handleDebitLinkedEntityChange = (entityId) => {
    let entityName = '';
    if (entryForm.debit_linked_entity_type === 'client') {
      const client = clients.find(c => c.id === entityId);
      entityName = client?.name || '';
    } else if (entryForm.debit_linked_entity_type === 'supplier') {
      const supplier = suppliers.find(s => s.id === entityId);
      entityName = supplier?.name || '';
    }
    setEntryForm(prev => ({
      ...prev,
      debit_linked_entity_id: entityId,
      debit_linked_entity_name: entityName
    }));
  };

  const handleCreditLinkedEntityChange = (entityId) => {
    let entityName = '';
    if (entryForm.credit_linked_entity_type === 'client') {
      const client = clients.find(c => c.id === entityId);
      entityName = client?.name || '';
    } else if (entryForm.credit_linked_entity_type === 'supplier') {
      const supplier = suppliers.find(s => s.id === entityId);
      entityName = supplier?.name || '';
    }
    setEntryForm(prev => ({
      ...prev,
      credit_linked_entity_id: entityId,
      credit_linked_entity_name: entityName
    }));
  };

  const resetForm = () => {
    setEntryForm({
      entry_date: new Date(),
      debit_classification_id: '',
      debit_classification_name: '',
      debit_linked_entity_type: null,
      debit_linked_entity_id: '',
      debit_linked_entity_name: '',
      debit_account_type: '',
      credit_classification_id: '',
      credit_classification_name: '',
      credit_linked_entity_type: null,
      credit_linked_entity_id: '',
      credit_linked_entity_name: '',
      credit_account_type: '',
      amount: '',
      remarks: ''
    });
  };

  const handleSaveEntry = async () => {
    // Validation
    if (!entryForm.entry_date) {
      toast.error('Please select a date');
      return;
    }
    if (!entryForm.debit_classification_id) {
      toast.error('Please select Debit classification');
      return;
    }
    if (!entryForm.credit_classification_id) {
      toast.error('Please select Credit classification');
      return;
    }
    if (!entryForm.amount || parseFloat(entryForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Check linked entities
    if (entryForm.debit_linked_entity_type && !entryForm.debit_linked_entity_id) {
      toast.error(`Please select ${entryForm.debit_linked_entity_type === 'client' ? 'a client' : 'a supplier'} for Debit`);
      return;
    }
    if (entryForm.credit_linked_entity_type && !entryForm.credit_linked_entity_id) {
      toast.error(`Please select ${entryForm.credit_linked_entity_type === 'client' ? 'a client' : 'a supplier'} for Credit`);
      return;
    }

    try {
      const payload = {
        entry_date: format(entryForm.entry_date, 'yyyy-MM-dd'),
        debit_classification_id: entryForm.debit_classification_id,
        debit_classification_name: entryForm.debit_classification_name,
        debit_linked_entity_type: entryForm.debit_linked_entity_type,
        debit_linked_entity_id: entryForm.debit_linked_entity_id || null,
        debit_linked_entity_name: entryForm.debit_linked_entity_name || null,
        debit_account_type: entryForm.debit_account_type,
        credit_classification_id: entryForm.credit_classification_id,
        credit_classification_name: entryForm.credit_classification_name,
        credit_linked_entity_type: entryForm.credit_linked_entity_type,
        credit_linked_entity_id: entryForm.credit_linked_entity_id || null,
        credit_linked_entity_name: entryForm.credit_linked_entity_name || null,
        credit_account_type: entryForm.credit_account_type,
        amount: parseFloat(entryForm.amount),
        remarks: entryForm.remarks || null
      };

      await axios.post(`${API_URL}/api/journal-entries`, payload, {
        headers: getAuthHeader()
      });

      toast.success('Journal entry recorded successfully');
      setEntryDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="journal-entries-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="w-7 h-7" />
              Journal Entries
            </h1>
            <p className="text-muted-foreground">Record debit and credit adjustments</p>
          </div>
          <Button onClick={() => setEntryDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Record Entry
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {dateFilter.start ? format(dateFilter.start, 'dd/MM/yyyy') : 'Start Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFilter.start}
                    onSelect={(date) => setDateFilter(prev => ({ ...prev, start: date }))}
                  />
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {dateFilter.end ? format(dateFilter.end, 'dd/MM/yyyy') : 'End Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFilter.end}
                    onSelect={(date) => setDateFilter(prev => ({ ...prev, end: date }))}
                  />
                </PopoverContent>
              </Popover>
              {(dateFilter.start || dateFilter.end) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setDateFilter({ start: null, end: null })}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Entries Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Debit To</TableHead>
                  <TableHead>Credit To</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No journal entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-sm">{formatDate(entry.entry_date)}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{entry.debit_classification_name}</span>
                          {entry.debit_linked_entity_name && (
                            <div className="text-xs text-muted-foreground">→ {entry.debit_linked_entity_name}</div>
                          )}
                          <Badge variant="outline" className="text-xs mt-1">{entry.debit_account_type}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{entry.credit_classification_name}</span>
                          {entry.credit_linked_entity_name && (
                            <div className="text-xs text-muted-foreground">→ {entry.credit_linked_entity_name}</div>
                          )}
                          <Badge variant="outline" className="text-xs mt-1">{entry.credit_account_type}</Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(entry.amount)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {entry.remarks || '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Record Entry Dialog */}
        <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Record Journal Entry</DialogTitle>
              <DialogDescription>Create a debit/credit adjustment entry</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Date and Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {entryForm.entry_date ? format(entryForm.entry_date, 'dd/MM/yyyy') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={entryForm.entry_date}
                        onSelect={(date) => setEntryForm(prev => ({ ...prev, entry_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={entryForm.amount}
                    onChange={(e) => setEntryForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Debit Section */}
              <div className="border rounded-lg p-4 bg-red-50/50">
                <Label className="text-base font-semibold text-red-700 mb-3 block">DEBIT TO (Dr.)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Classification *</Label>
                    <SearchableSelect
                      options={classificationOptions}
                      value={entryForm.debit_classification_id}
                      onValueChange={handleDebitClassificationChange}
                      placeholder="Select Classification..."
                      searchPlaceholder="Search..."
                      emptyText="No classifications"
                    />
                    {entryForm.debit_account_type && (
                      <Badge variant="outline" className="text-xs">{entryForm.debit_account_type}</Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Linked To</Label>
                    {entryForm.debit_linked_entity_type === 'client' ? (
                      <SearchableSelect
                        options={clientOptions}
                        value={entryForm.debit_linked_entity_id}
                        onValueChange={handleDebitLinkedEntityChange}
                        placeholder="Select Client..."
                        searchPlaceholder="Search clients..."
                        emptyText="No clients"
                      />
                    ) : entryForm.debit_linked_entity_type === 'supplier' ? (
                      <SearchableSelect
                        options={supplierOptions}
                        value={entryForm.debit_linked_entity_id}
                        onValueChange={handleDebitLinkedEntityChange}
                        placeholder="Select Supplier..."
                        searchPlaceholder="Search suppliers..."
                        emptyText="No suppliers"
                      />
                    ) : (
                      <div className="h-10 flex items-center text-muted-foreground text-sm">
                        No linking required
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Arrow between sections */}
              <div className="flex justify-center">
                <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
              </div>

              {/* Credit Section */}
              <div className="border rounded-lg p-4 bg-green-50/50">
                <Label className="text-base font-semibold text-green-700 mb-3 block">CREDIT TO (Cr.)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Classification *</Label>
                    <SearchableSelect
                      options={classificationOptions}
                      value={entryForm.credit_classification_id}
                      onValueChange={handleCreditClassificationChange}
                      placeholder="Select Classification..."
                      searchPlaceholder="Search..."
                      emptyText="No classifications"
                    />
                    {entryForm.credit_account_type && (
                      <Badge variant="outline" className="text-xs">{entryForm.credit_account_type}</Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Linked To</Label>
                    {entryForm.credit_linked_entity_type === 'client' ? (
                      <SearchableSelect
                        options={clientOptions}
                        value={entryForm.credit_linked_entity_id}
                        onValueChange={handleCreditLinkedEntityChange}
                        placeholder="Select Client..."
                        searchPlaceholder="Search clients..."
                        emptyText="No clients"
                      />
                    ) : entryForm.credit_linked_entity_type === 'supplier' ? (
                      <SearchableSelect
                        options={supplierOptions}
                        value={entryForm.credit_linked_entity_id}
                        onValueChange={handleCreditLinkedEntityChange}
                        placeholder="Select Supplier..."
                        searchPlaceholder="Search suppliers..."
                        emptyText="No suppliers"
                      />
                    ) : (
                      <div className="h-10 flex items-center text-muted-foreground text-sm">
                        No linking required
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={entryForm.remarks}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Optional remarks..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setEntryDialogOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleSaveEntry}>Save Entry</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
